import { UseFormReturn } from 'react-hook-form'
import { Divider } from 'utoniq-ui'

import {
  Gender,
  Maybe,
  PaymentDetailFragment,
  PersonalInfoFragment,
} from '@/clients/utoniq-core/schema'
import { tLabel } from '@/core'
import { Organism, OrganismFrame } from '@/core/architecture'
import { UseFormProps } from '@/core/form'

import {
  AddressInputCell,
  BankAccountCell,
  BirthdayInputCell,
  ButtonCell,
  GenderInputCell,
  PaymentMethodCell,
  PhoneNumberInputCell,
  TextInputCell,
  UserAccountCell,
} from './components'

export type PersonalInfoFormProps = {
  user?: Maybe<PersonalInfoFragment>
  isMembershipPersonalInformationRequired: boolean
  visibleMoreInfo?: boolean
  paymentSource: Maybe<PaymentDetailFragment>
  submitting: boolean
  personalInfoForm: UseFormReturn<SettingValues>
  onSubmit: (value: SettingValues) => Promise<void | boolean>
  sendingSMSCode: boolean
  verifyingSMSCode: boolean
  needVerifyPhoneNumber: boolean
  onVerificationPhoneNumber: (values: { code: string }) => Promise<void>
  onChangeNeedVerifyPhoneNumber: (needVerifyPhoneNumber: boolean) => void
  onSendVerificationSMS: () => Promise<void>
  onResendVerificationSMS: () => Promise<void>
}

export type SettingValues = {
  fullName: string
  phoneNumber: string
  birthday: Date | null
  gender: Gender | null
  postalCode: string
  prefecture: string
  city: string
  line1: string
  line2: string
  isMembershipPersonalInformationRequired: boolean
}

export const schema: UseFormProps['schema'] = o => ({
  fullName: o.string().when('isMembershipPersonalInformationRequired', {
    is: true,
    then: o.string().max(50).label(tLabel('personalInfo.fullName')),
    otherwise: o
      .string()
      .max(50)
      .optional()
      .allow('')
      .label(tLabel('personalInfo.fullName')),
  }),
  phoneNumber: o.string().when('isMembershipPersonalInformationRequired', {
    is: true,
    then: o.string().max(15).label(tLabel('personalInfo.phoneNumber')),
    otherwise: o
      .string()
      .max(15)
      .optional()
      .allow('')
      .label(tLabel('personalInfo.phoneNumber')),
  }),
  gender: o.string().when('isMembershipPersonalInformationRequired', {
    is: true,
    then: o.string().label(tLabel('personalInfo.gender')),
    otherwise: o
      .string()
      .optional()
      .allow(null)
      .label(tLabel('personalInfo.gender')),
  }),
  birthday: o.date().when('isMembershipPersonalInformationRequired', {
    is: true,
    then: o.date().label(tLabel('personalInfo.birthday')),
    otherwise: o
      .date()
      .optional()
      .allow(null)
      .label(tLabel('personalInfo.birthday')),
  }),
  postalCode: o.string().when('isMembershipPersonalInformationRequired', {
    is: true,
    then: o
      .string()
      .regex(/^\d{7}$/)
      .label(tLabel('personalInfo.postalCode')),
    otherwise: o
      .string()
      .regex(/^\d{7}$/)
      .optional()
      .allow(null, '')
      .label(tLabel('personalInfo.postalCode')),
  }),
  prefecture: o.string().when('isMembershipPersonalInformationRequired', {
    is: true,
    then: o.string().max(150).label(tLabel('personalInfo.prefecture')),
    otherwise: o
      .string()
      .optional()
      .allow(null, '')
      .max(150)
      .label(tLabel('personalInfo.prefecture')),
  }),
  city: o.string().when('isMembershipPersonalInformationRequired', {
    is: true,
    then: o.string().max(150).label(tLabel('personalInfo.city')),
    otherwise: o
      .string()
      .optional()
      .allow(null, '')
      .max(150)
      .label(tLabel('personalInfo.city')),
  }),
  line1: o.string().when('isMembershipPersonalInformationRequired', {
    is: true,
    then: o.string().max(150).label(tLabel('personalInfo.line1')),
    otherwise: o
      .string()
      .optional()
      .allow(null, '')
      .max(150)
      .label(tLabel('personalInfo.line1')),
  }),
  line2: o
    .string()
    .optional()
    .allow(null, '')
    .max(150)
    .label(tLabel('personalInfo.line2')),
  isMembershipPersonalInformationRequired: o.boolean(),
})

export const PersonalInfoForm: Organism<PersonalInfoFormProps> = ({
  submitting,
  user,
  visibleMoreInfo,
  paymentSource,
  isMembershipPersonalInformationRequired,
  onSubmit,
  personalInfoForm,
  sendingSMSCode,
  verifyingSMSCode,
  needVerifyPhoneNumber,
  onChangeNeedVerifyPhoneNumber,
  onVerificationPhoneNumber,
  onSendVerificationSMS,
  onResendVerificationSMS,
}) => {
  const {
    register,
    formState: { errors, isValid, isSubmitting },
    watch,
    setValue,
    handleSubmit,
  } = personalInfoForm

  const onSubmitForm = handleSubmit(values => onSubmit(values))

  return (
    <OrganismFrame sx={{ pt: 3 }}>
      <TextInputCell
        type="text"
        {...register('fullName')}
        error={errors?.fullName}
        isRequired={isMembershipPersonalInformationRequired}
      />
      <Divider />
      <BirthdayInputCell
        value={watch('birthday')}
        setValue={setValue}
        error={errors?.birthday}
        isRequired={isMembershipPersonalInformationRequired}
      />
      <Divider />
      <GenderInputCell
        value={watch('gender')}
        setValue={setValue}
        isRequired={isMembershipPersonalInformationRequired}
      />
      {visibleMoreInfo && (
        <>
          <Divider />
          <UserAccountCell user={user} />
        </>
      )}
      <Divider />
      <PhoneNumberInputCell
        isRequired={isMembershipPersonalInformationRequired}
        isPhoneNumberVerified={!!user?.isPhoneNumberVerified}
        formattedPhoneNumber={user?.formattedPhoneNumber ?? ''}
        inputProps={{ ...register('phoneNumber'), error: errors.phoneNumber }}
        phoneNumber={watch('phoneNumber')}
        sendingSMSCode={sendingSMSCode}
        verifyingSMSCode={verifyingSMSCode}
        needVerifyPhoneNumber={needVerifyPhoneNumber}
        onChangeNeedVerifyPhoneNumber={onChangeNeedVerifyPhoneNumber}
        onVerificationPhoneNumber={onVerificationPhoneNumber}
        onSendVerificationSMS={onSendVerificationSMS}
        onResendVerificationSMS={onResendVerificationSMS}
      />
      <Divider />
      <AddressInputCell
        register={register}
        errors={errors}
        isRequired={isMembershipPersonalInformationRequired}
      />
      {visibleMoreInfo && (
        <>
          <Divider />
          <PaymentMethodCell paymentSource={paymentSource} />
          <Divider />
          <BankAccountCell bank={user?.bank} />
        </>
      )}
      <ButtonCell
        disabled={!isValid || isSubmitting}
        loading={isSubmitting || submitting}
        onSubmit={onSubmitForm}
      />
    </OrganismFrame>
  )
}

export const defaultValues: SettingValues = {
  gender: null,
  birthday: null,
  fullName: '',
  phoneNumber: '',
  postalCode: '',
  prefecture: '',
  city: '',
  line1: '',
  line2: '',
  isMembershipPersonalInformationRequired: false,
}
